/* eslint-disable consistent-return */
import { useState, useMemo, useEffect } from 'react';
import { ClinicalMediaType } from '@/api/apis/case';
import { getFileUrl } from '@/api/utils';

type UseMediaImageLoaderProps = {
  currentMedia?: ClinicalMediaType;
  open: boolean;
  useDirectPath?: boolean;
};

export const useMediaImageLoader = ({ currentMedia, open, useDirectPath = false }: UseMediaImageLoaderProps) => {
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [loadedImageSrc, setLoadedImageSrc] = useState<string | null>(null);

  const currentImageSrc = useMemo(() => {
    if (!currentMedia || currentMedia.file.mediaType === 'Video') {
      return null;
    }

    return useDirectPath ? currentMedia.file.path : getFileUrl(currentMedia.file.path);
  }, [currentMedia, useDirectPath]);

  useEffect(() => {
    if (!open || !currentMedia) {
      setIsImageLoading(false);
      setLoadedImageSrc(null);
      return;
    }

    if (currentMedia.file.mediaType === 'Video') {
      setIsImageLoading(false);
      setLoadedImageSrc(null);
      return;
    }

    if (!currentImageSrc) {
      setIsImageLoading(false);
      setLoadedImageSrc(null);
      return;
    }

    let isCancelled = false;

    setIsImageLoading(true);
    setLoadedImageSrc(null);

    const image = new Image();

    image.onload = () => {
      if (isCancelled) return;
      setLoadedImageSrc(currentImageSrc);
      setIsImageLoading(false);
    };

    image.onerror = () => {
      if (isCancelled) return;
      setLoadedImageSrc(currentImageSrc);
      setIsImageLoading(false);
    };

    image.src = currentImageSrc;

    return () => {
      isCancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [currentImageSrc, currentMedia, open]);

  return {
    currentImageSrc,
    isImageLoading,
    loadedImageSrc,
  };
};
